import { useQuery } from '@tanstack/react-query'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { keys } from '@/api/hooks/shared/keys'
import {
  isBeneathRoot,
  nodeToReportAccount,
  reportAccountsFromTree,
} from '@/features/reports/hierarchyAccounts'
import type { ReportAccount } from '@/features/reports/types'
import type { HierarchyNode } from '@/features/hierarchy/hierarchy'

export function useReportSelf() {
  return useQuery({
    queryKey: keys.reportHierarchySelf,
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
    staleTime: 60_000,
  })
}

export function useReportHierarchyTree() {
  return useQuery({
    queryKey: keys.reportHierarchyTree,
    queryFn: () => cmApi.hierarchyTree(authHeaders()),
    staleTime: 60_000,
  })
}

export function selfAsReportAccount(
  self: { node: HierarchyNode } | undefined,
  treeNodes: HierarchyNode[],
): ReportAccount | undefined {
  if (!self?.node) return undefined
  const nodes = treeNodes.length ? treeNodes : [self.node]
  return nodeToReportAccount(self.node, nodes, self.node.id)
}

export function useReportScopeAccount(scopeId: string | undefined) {
  const selfQuery = useReportSelf()
  const treeQuery = useReportHierarchyTree()

  return useQuery({
    queryKey: keys.reportScopeAccount(scopeId ?? ''),
    enabled: Boolean(scopeId && selfQuery.data?.node && treeQuery.data),
    queryFn: async (): Promise<ReportAccount | null> => {
      const self = selfQuery.data
      const tree = treeQuery.data
      if (!scopeId || !self?.node) return null
      if (scopeId === self.node.id) return selfAsReportAccount(self, tree?.nodes ?? []) ?? null

      const nodes = tree?.nodes ?? []
      if (!isBeneathRoot(nodes, self.node.id, scopeId)) {
        try {
          const agent = await cmApi.hierarchyAgent(authHeaders(), scopeId)
          if (!agent?.node) return null
          const merged = [...nodes, agent.node]
          if (!isBeneathRoot(merged, self.node.id, scopeId)) return null
          const map = reportAccountsFromTree(merged, self.node)
          return map.get(scopeId) ?? null
        } catch {
          return null
        }
      }

      const map = reportAccountsFromTree(nodes, self.node)
      return map.get(scopeId) ?? null
    },
    staleTime: 30_000,
  })
}
